import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useLocale } from '@/contexts/LocaleContext';
import useTranslation from '@/hooks/useTranslation';
import { CartProvider, useCart } from '@/contexts/CartContext';
import CartDrawer from '@/components/store/CartDrawer';
import ProductModal from '@/components/store/ProductModal';
import { getFirstImage } from '@/utils/imageUtils';
import { 
  Store, Package, MapPin, CheckCircle2, Crown, Star,
  MessageSquare, FileText, Package2, Loader2, Search,
  Image as ImageIcon, ExternalLink, Phone, Mail, ArrowLeft,
  Filter, X, Share2, Copy, Check, ShoppingCart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicStorePage = () => {
  const { slug } = useParams();
  const { isUSA, formatPrice } = useLocale();
  const { t } = useTranslation();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactType, setContactType] = useState('contacto');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [processing, setProcessing] = useState(false);

  const [contactForm, setContactForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    message: '',
    quantity: ''
  });

  const [copied, setCopied] = useState(false);
  const [showPromoBanner, setShowPromoBanner] = useState(true);

  // Share URL goes through the backend so WhatsApp/Facebook get Open Graph tags (title, image, description)
  const storeUrl = store?.slug ? `${API_URL}/api/stores/share/${store.slug}` : (typeof window !== 'undefined' ? window.location.href : '');
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent((isUSA ? `Check out ${store?.store_name} on TexHub: ` : `Mira el catálogo de ${store?.store_name} en TexHub: `) + storeUrl)}`;
  const referralRegisterUrl = `/register${store?.owner_referral_code ? `?ref=${store.owner_referral_code}` : ''}`;

  useEffect(() => {
    if (!store) return;
    document.title = `${store.store_name} | TexHub`;
    return () => { document.title = 'TexHub'; };
  }, [store]);

  const handleShare = async () => {
    const shareData = {
      title: store?.store_name || 'TexHub Store',
      text: isUSA 
        ? `Check out ${store?.store_name} on TexHub!` 
        : `¡Conoce ${store?.store_name} en TexHub!`,
      url: storeUrl
    };

    // Try native share first (mobile)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share failed:', err);
        }
      }
    }

    // Fallback: copy to clipboard
    handleCopyLink();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(storeUrl).then(() => {
      setCopied(true);
      toast.success(isUSA ? 'Link copied to clipboard!' : '¡Enlace copiado al portapapeles!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error(isUSA ? 'Could not copy link' : 'No se pudo copiar el enlace');
    });
  };

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await fetch(`${API_URL}/api/stores/public/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setStore(data);
        } else if (res.status === 404) {
          setError(isUSA ? 'Store not found' : 'Tienda no encontrada');
        } else {
          setError('Error al cargar la tienda');
        }
      } catch (err) {
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [slug, isUSA]);

  const handleContact = async () => {
    if (!contactForm.contact_name || !contactForm.contact_email || !contactForm.message) {
      toast.error(isUSA ? 'Name, email and message are required' : 'Nombre, email y mensaje son requeridos');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/stores/${store.store_id}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_type: contactType,
          product_id: selectedProduct?.product_id || null,
          message: contactForm.message,
          contact_name: contactForm.contact_name,
          contact_email: contactForm.contact_email,
          contact_phone: contactForm.contact_phone || null,
          quantity: contactForm.quantity ? parseInt(contactForm.quantity) : null
        })
      });

      if (res.ok) {
        toast.success(isUSA ? 'Request sent! The seller will contact you soon.' : '¡Solicitud enviada! El vendedor te contactará pronto.');
        setShowContactDialog(false);
        setContactForm({ contact_name: '', contact_email: '', contact_phone: '', message: '', quantity: '' });
        setSelectedProduct(null);
      } else {
        toast.error(isUSA ? 'Error sending request' : 'Error al enviar solicitud');
      }
    } catch (err) {
      toast.error(isUSA ? 'Connection error' : 'Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  const openContactDialog = (type, product = null) => {
    setContactType(type);
    setSelectedProduct(product);
    setShowContactDialog(true);
  };

  const filteredProducts = store?.products?.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) || [];

  const categories = [...new Set(store?.products?.map(p => p.category) || [])];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">{error}</h1>
            <p className="text-slate-600 mb-4">
              {isUSA ? 'The store you are looking for does not exist or is not available.' : 'La tienda que buscas no existe o no está disponible.'}
            </p>
            <Link to="/tiendas">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {isUSA ? 'Explore stores' : 'Explorar tiendas'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/tiendas" className="text-emerald-600 font-bold text-xl flex items-center gap-2">
            <Store className="w-5 h-5" />
            TexHub
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">{isUSA ? 'Log in' : 'Iniciar sesión'}</Button>
          </Link>
        </div>
      </header>

      {/* "Made with TexHub" badge - free plan stores */}
      {store.show_texhub_badge && (
        <div className="bg-slate-900 text-white" data-testid="texhub-free-store-badge">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm">
            <Store className="w-4 h-4 text-emerald-400" />
            <span className="text-slate-300">{isUSA ? 'Made with' : 'Hecho con'} <span className="font-bold text-white">TexHub</span></span>
            <span className="text-slate-500 hidden sm:inline">·</span>
            <Link to={referralRegisterUrl} className="font-semibold text-emerald-400 hover:text-emerald-300 underline underline-offset-2" data-testid="texhub-free-store-badge-link">
              {isUSA ? 'create your free store' : 'crea tu tienda gratis'}
            </Link>
          </div>
        </div>
      )}

      {/* Promotional Banner for non-registered visitors */}
      {showPromoBanner && !store.show_texhub_badge && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 relative">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
            <Store className="w-5 h-5 flex-shrink-0" />
            <p className="text-center">
              {isUSA 
                ? '✨ You can also have your own online store! Join TexHub and start selling today.' 
                : '✨ ¡Tú también puedes tener tu propia tienda en línea! Únete a TexHub y comienza a vender hoy.'}
            </p>
            <Link to="/register" className="flex-shrink-0">
              <Button size="sm" className="bg-white text-emerald-600 hover:bg-white/90 font-semibold">
                {isUSA ? 'Register free' : 'Regístrate gratis'}
              </Button>
            </Link>
            <button 
              onClick={() => setShowPromoBanner(false)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Promotional Banner Text */}
      {store.banner_text && (
        <div 
          className="text-white text-center py-2 px-4 text-sm font-medium"
          style={{ backgroundColor: store.primary_color || '#059669' }}
        >
          {store.banner_text}
        </div>
      )}

      {/* Store Banner */}
      <div 
        className="text-white relative"
        style={{ 
          background: store.cover_url 
            ? `linear-gradient(to right, ${store.primary_color || '#059669'}dd, ${store.secondary_color || '#10B981'}dd)`
            : `linear-gradient(to right, ${store.primary_color || '#059669'}, ${store.secondary_color || '#10B981'})`
        }}
      >
        {/* Cover Image Background */}
        {store.cover_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${store.cover_url})`,
              zIndex: 0
            }}
          />
        )}
        {/* Overlay for readability */}
        {store.cover_url && (
          <div 
            className="absolute inset-0"
            style={{ 
              background: `linear-gradient(to right, ${store.primary_color || '#059669'}cc, ${store.secondary_color || '#10B981'}cc)`,
              zIndex: 1
            }}
          />
        )}
        
        <div className="max-w-7xl mx-auto px-4 py-8 relative" style={{ zIndex: 2 }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Logo */}
            <div className="w-24 h-24 bg-white rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0 shadow-lg">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.store_name} className="w-full h-full object-cover" />
              ) : (
                <Store className="w-12 h-12" style={{ color: store.primary_color || '#059669' }} />
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h1 className="text-2xl sm:text-3xl font-bold">{store.store_name}</h1>
                {store.is_featured && (
                  <Badge className="bg-yellow-400 text-yellow-900">
                    <Crown className="w-3 h-3 mr-1" />
                    {isUSA ? 'Featured' : 'Destacada'}
                  </Badge>
                )}
                {store.company?.verified && (
                  <Badge className="bg-white/20 text-white">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {isUSA ? 'Verified' : 'Verificada'}
                  </Badge>
                )}
              </div>
              {store.description && (
                <p className="text-white/90 mb-2">{store.description}</p>
              )}
              {store.company && (
                <div className="flex items-center gap-4 text-sm text-white/80">
                  <span className="flex items-center gap-1">
                    <Store className="w-4 h-4" />
                    {store.company.name}
                  </span>
                  {store.company.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {store.company.city}, {store.company.state}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={handleShare}
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                title={isUSA ? 'Share store' : 'Compartir tienda'}
              >
                {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                <span className="hidden sm:inline ml-2">{isUSA ? 'Share' : 'Compartir'}</span>
              </Button>
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-md px-3 h-10 text-sm font-medium bg-[#25D366] text-white hover:bg-[#1ebe5b] transition-colors"
                title={isUSA ? 'Share on WhatsApp' : 'Compartir por WhatsApp'}
                data-testid="store-share-whatsapp-btn"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">WhatsApp</span>
              </a>
              <Button 
                onClick={() => openContactDialog('contacto')}
                className="bg-white text-emerald-600 hover:bg-white/90"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {isUSA ? 'Contact supplier' : 'Contactar proveedor'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder={isUSA ? 'Search products...' : 'Buscar productos...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {categories.length > 1 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder={isUSA ? 'All categories' : 'Todas las categorías'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{isUSA ? 'All categories' : 'Todas las categorías'}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Products Count */}
        <p className="text-sm text-slate-600 mb-4">
          {filteredProducts.length} {isUSA ? 'product' : 'producto'}{filteredProducts.length !== 1 ? 's' : ''} {isUSA ? 'found' : 'encontrado'}{filteredProducts.length !== 1 ? 's' : ''}
        </p>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">{isUSA ? 'No products' : 'No hay productos'}</h3>
              <p className="text-slate-600">{isUSA ? 'This store has no products available yet.' : 'Esta tienda aún no tiene productos disponibles.'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.product_id} className="overflow-hidden hover:shadow-lg transition-shadow group">
                {/* Image */}
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img 
                      src={getFirstImage(product.images)} 
                      alt={product.name} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                  {product.moq && (
                    <Badge className="absolute top-2 left-2 bg-slate-900/70 text-white">
                      MOQ: {product.moq}
                    </Badge>
                  )}
                </div>

                {/* Content */}
                <CardContent className="p-4">
                  <Badge variant="outline" className="text-xs mb-2">{product.category}</Badge>
                  <h3 className="font-semibold text-slate-900 line-clamp-2 mb-1">{product.name}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{product.description}</p>
                  
                  {product.price_visible && product.price ? (
                    <p className="text-lg font-bold text-emerald-600 mb-3">{formatPrice(product.price)}</p>
                  ) : (
                    <p className="text-sm text-slate-500 mb-3">{isUSA ? 'Price on request' : 'Precio a consultar'}</p>
                  )}

                  {/* Tags */}
                  {product.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowProductModal(true);
                      }}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      {isUSA ? 'Buy' : 'Comprar'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openContactDialog('cotizacion', product)}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {contactType === 'cotizacion' 
                ? (isUSA ? 'Request Quote' : 'Solicitar Cotización')
                : contactType === 'muestra' 
                  ? (isUSA ? 'Request Sample' : 'Solicitar Muestra')
                  : (isUSA ? 'Contact Supplier' : 'Contactar Proveedor')}
            </DialogTitle>
            <DialogDescription>
              {selectedProduct 
                ? `${isUSA ? 'Product' : 'Producto'}: ${selectedProduct.name}` 
                : `${isUSA ? 'Store' : 'Tienda'}: ${store?.store_name}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{isUSA ? 'Your name' : 'Tu nombre'} *</Label>
              <Input 
                value={contactForm.contact_name}
                onChange={(e) => setContactForm({...contactForm, contact_name: e.target.value})}
                placeholder={isUSA ? 'John Smith' : 'Juan Pérez'}
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                value={contactForm.contact_email}
                onChange={(e) => setContactForm({...contactForm, contact_email: e.target.value})}
                placeholder={isUSA ? 'you@email.com' : 'tu@email.com'}
              />
            </div>

            <div className="space-y-2">
              <Label>{isUSA ? 'Phone (optional)' : 'Teléfono (opcional)'}</Label>
              <Input 
                type="tel"
                value={contactForm.contact_phone}
                onChange={(e) => setContactForm({...contactForm, contact_phone: e.target.value})}
                placeholder={isUSA ? '+1 555 123 4567' : '55 1234 5678'}
              />
            </div>

            {(contactType === 'cotizacion' || contactType === 'muestra') && (
              <div className="space-y-2">
                <Label>{isUSA ? 'Quantity' : 'Cantidad'} {contactType === 'muestra' ? (isUSA ? 'of samples' : 'de muestras') : ''}</Label>
                <Input 
                  type="number"
                  value={contactForm.quantity}
                  onChange={(e) => setContactForm({...contactForm, quantity: e.target.value})}
                  placeholder={contactType === 'muestra' ? '1' : (isUSA ? 'Ex: 500' : 'Ej: 500')}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{isUSA ? 'Message' : 'Mensaje'} *</Label>
              <Textarea 
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                placeholder={
                  contactType === 'cotizacion' 
                    ? (isUSA ? 'Describe your requirements, colors, quantities...' : 'Describe tus requerimientos, colores, cantidades...')
                    : contactType === 'muestra' 
                      ? (isUSA ? 'Indicate what samples you need and what you will use them for...' : 'Indica qué muestras necesitas y para qué las usarás...')
                      : (isUSA ? 'Write your message...' : 'Escribe tu mensaje...')
                }
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>
              {isUSA ? 'Cancel' : 'Cancelar'}
            </Button>
            <Button onClick={handleContact} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Send request' : 'Enviar solicitud')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            {isUSA 
              ? <>Store on <Link to="/tiendas" className="text-emerald-400 hover:underline">TexHub</Link> - The leading B2B platform for the textile industry</>
              : <>Tienda en <Link to="/tiendas" className="text-emerald-400 hover:underline">TexHub</Link> - La plataforma B2B líder de la industria textil en México</>
            }
          </p>
          {store.show_texhub_badge && (
            <Link to={referralRegisterUrl} className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-sm font-medium transition-colors" data-testid="texhub-free-store-footer-badge">
              <Store className="w-4 h-4 text-emerald-400" />
              {isUSA ? 'Made with TexHub – create your free store' : 'Hecho con TexHub – crea tu tienda gratis'}
            </Link>
          )}
        </div>
      </footer>

      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct}
        isOpen={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          setSelectedProduct(null);
        }}
        isUSA={isUSA}
      />

      {/* Cart Drawer */}
      <CartDrawer 
        storeId={store?.store_id} 
        storeName={store?.store_name}
        isUSA={isUSA}
      />

      {/* Floating Cart Button */}
      <FloatingCartButton />
    </div>
  );
};

// Floating Cart Button Component
const FloatingCartButton = () => {
  const { setIsOpen, itemCount } = useCart();
  
  if (itemCount === 0) return null;
  
  return (
    <Button
      onClick={() => setIsOpen(true)}
      className="fixed bottom-20 right-4 z-50 h-14 w-14 rounded-full shadow-lg bg-emerald-600 hover:bg-emerald-700"
      data-testid="floating-cart-btn"
    >
      <ShoppingCart className="w-6 h-6" />
      {itemCount > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
          {itemCount > 9 ? '9+' : itemCount}
        </span>
      )}
    </Button>
  );
};

// Wrapper with Cart Provider
const PublicStorePageWrapper = () => {
  const { slug } = useParams();
  const [storeId, setStoreId] = useState(null);
  
  // We need to fetch store first to get the ID for CartProvider
  useEffect(() => {
    const fetchStoreId = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${API_URL}/api/stores/public/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setStoreId(data.store_id);
        }
      } catch (error) {
        console.error('Error fetching store:', error);
      }
    };
    fetchStoreId();
  }, [slug]);
  
  return (
    <CartProvider storeId={storeId}>
      <PublicStorePage />
    </CartProvider>
  );
};

export default PublicStorePageWrapper;
